<template>
  <div class="body application-details-container">
    <div class="details">
      <div class="configuration">
        <div :class="$style.sectionRoot">
          <header
            :class="[$style.sectionHeader, $style.sectionGeneralMargin]"
          >
            {{ sectionHeading }}
          </header>

          <ApplicationOIDCFields v-if="item.sso.type === 'oidc'" />

          <template v-else>
            <div
              v-if="!item.isBookmark"
              :class="['learn-more', $style.infoContainer]"
            >
              <KnowledgeBaseArticle
                v-if="isItemBeingCreated"
                :class="$style.sectionGeneralMargin"
                infoText="An IDP Certificate and Private Key will be generated for this application after activation."
                :kbLink="learnMoreHref"
                kbText="Click here to see the Knowledge Base article with details for configuring this application"
              />
              <KnowledgeBaseArticle
                v-else
                :includeInfoIcon="false"
                :infoText="$options.learnMore.infoText"
                :kbLink="learnMoreHref"
                :kbText="$options.learnMore.kbText"
              />
            </div>

            <div
              v-if="item.beta"
              :class="['beta-tag', $style.infoContainer]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 27 10" v-bind:class="'beta-img'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M5.522 5.383h-1.56v1.8h1.56a1.434 1.434 0 00.874-.214.796.796 0 00.277-.673.834.834 0 00-.277-.691 1.395 1.395 0 00-.874-.222zM6.52 3.67a.752.752 0 00-.282-.64 1.425 1.425 0 00-.86-.213H3.962v1.706h1.415q1.142 0 1.142-.852z" fill="#353f44" fill-rule="evenodd"/><path d="M25.744 0H1.256A1.256 1.256 0 000 1.256v7.488A1.256 1.256 0 001.256 10h24.488A1.256 1.256 0 0027 8.744V1.256A1.256 1.256 0 0025.744 0zM7.171 7.596a2.394 2.394 0 01-1.53.439H2.888v-6.07h2.668a2.323 2.323 0 011.474.423 1.397 1.397 0 01.537 1.163 1.4 1.4 0 01-.25.831 1.425 1.425 0 01-.695.516 1.506 1.506 0 01.813.536 1.541 1.541 0 01.286.947 1.468 1.468 0 01-.55 1.215zm5.367-4.76H9.555v1.679h2.804v.868H9.555v1.782h2.983v.87H8.481v-6.07h4.057zm3.843 5.199h-1.108V2.87h-1.978v-.905h5.072v.905h-1.986zm6.63 0l-.604-1.424h-2.966l-.614 1.423h-1.108l2.745-6.069h.895l2.753 6.07z" fill="#353f44" fill-rule="evenodd"/><path fill="#353f44" fill-rule="evenodd" d="M19.807 5.759h2.234l-1.117-2.6-1.117 2.6z"/></svg>
              <p>
                We're evaluating Beta connectors in various real-world environments
                so we can gather feedback to enhance their performance.
              </p>
            </div>

            <div
              v-if="metadataAction"
              :class="$style.exportMetadataContainer"
            >
              <label
                :class="[
                  $style.label,
                  $style['label-font'],
                  $style['label-color'],
                  $style['label-typography'],
                ]"
              >
                JumpCloud Metadata:
              </label>
              <div
                :class="$style.metadataActionButtonsContainer"
              >
                <IconButton
                  aria-label="Export Metadata"
                  :hasOutline="true"
                  icon="fileDownload"
                  iconPosition="right"
                  :text="metadataAction.exportMetadata.text"
                  type="secondary"
                  @click="metadataAction.exportMetadata.handleClick"
                />
                <IconButton
                  v-if="$hasFeatureFlag($featureFlags.publicUrlMetadata)"
                  aria-label="Copy Metadata URL"
                  :hasOutline="true"
                  icon="copy"
                  iconPosition="right"
                  :text="metadataAction.copyMetadataUrl.text"
                  type="secondary"
                  @click="metadataAction.copyMetadataUrl.handleClick"
                />
              </div>
            </div>

            <FileInput
              v-if="!item.isBookmark"
              accept="text/xml,application/xml,.xml'"
              buttonText="Upload Metadata"
              :class="$style.uploadMetadataContainer"
              :handleValueChange="handleFileChange"
              label="Service Provider Metadata:"
              :tooltip="fileInputTooltip"
            />

            <div
              v-if="!item.isBookmark"
              class="dynamic-controls main-dynamic-controls-container"
            >
              <ApplicationDetailsMainDynamicControls
                :key="dynamicComponentKey"
                ref="mainDynamicControls"
                :class="$style.metadataInput"
                :enableMultiAcs="enableMultiAcs"
                :parentType="parentType"
              />
            </div>

            <div class="idp-url">
              <div class="control-group">
                <label
                  :class="[$style.label, 'control-label']"
                >{{ !item.isBookmark ? 'IDP ' : '' }}URL:</label>

                <div
                  v-if="isItemBeingUpdated || item.isBookmark"
                  class="controls command-text"
                >
                  <InputOptionalValidation
                    ref="ssoUrl"
                    v-validate="`required${item.isBookmark ? '|isValidUrl' : ''}`"
                    class="idp-input"
                    :name="item.isBookmark ? 'URL' : 'IDP URL'"
                    :placeholder="item.isBookmark ? 'Application url' : ''"
                    :readonly="!item.isBookmark && isItemBeingUpdated"
                    :value="item.ssoUrl"
                    @input="(value) => setItemField({ key: 'ssoUrl', value })"
                  />
                </div>

                <div
                  v-else
                  :class="['controls command-text', $style.ssoDirNameContainer]"
                >
                  <span class="idp-text">
                    {{ item.ssoDirName }}
                  </span>
                  <InputOptionalValidation
                    ref="ssoUrl"
                    v-validate="'required|isValidSSOFileName'"
                    class="idp-input"
                    name="IDP URL"
                    :value="item.ssoFileName"
                    @input="(value) => setItemField({ key: 'ssoFileName', value })"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="!item.isBookmark"
              class="dynamic-controls dynamic-controls-attributes-header"
            >
              <h2><strong>Attributes</strong></h2>

              <p
                v-if="isJitSupported"
              >
                If attributes are required by this Service Provider for SSO authentication,
                they are not editable.
                Those required for SAML Just-in-Time Provisioning may be toggled off.
                Additional attributes may be included in assertions,
                although support for each attribute will vary for each Service Provider.
                <a
                  class="help-text-highlight"
                  :href="learnMoreJITHref"
                  rel="noopener noreferrer"
                  target="_blank"
                >&nbsp;Learn more.</a>
              </p>
              <p
                v-else
              >
                If attributes are required by this Service Provider for SSO authentication,
                they are not editable.
                Additional attributes may be included in assertions,
                although support for each attribute will vary for each Service Provider.
                <a
                  class="help-text-highlight"
                  :href="learnMoreJITHref"
                  rel="noopener noreferrer"
                  target="_blank"
                >&nbsp;Learn more.</a>
              </p>
            </div>

            <div
              v-if="!item.isBookmark"
              class="secondary-dynamic-controls-container"
            >
              <ApplicationDetailsSecondaryDynamicControls
                :key="dynamicComponentKey"
                ref="secondaryDynamicControls"
                :parentType="parentType"
              />
            </div>

            <FlatActionButton
              v-if="isItemBeingUpdated"
              :action="handleApplicationState"
              :buttonClass="buttonStyleApplicationState"
              :text="buttonTextApplicationState"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { cloneDeep, some } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { notify } from 'client/util';

import { IconButton } from '@jumpcloud/ui-components';
import { samlMetadata } from 'client/util/Constants/ApplicationConstants';

import ApplicationApi from 'client/api/v2/ApplicationApi';
import ApplicationConfigCertMixin from 'client/applications/mixins/ApplicationConfigCertMixin';
import ApplicationDetailsMainDynamicControls from 'client/applications/ApplicationDetailsMainDynamicControls.vue';
import ApplicationDetailsSecondaryDynamicControls from 'client/applications/ApplicationDetailsSecondaryDynamicControls.vue';
import ApplicationOIDCFields from 'client/applications/ApplicationOIDCFields.vue';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import FileInput from 'client/components/FormValidation/FileInput.vue';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconTooltipSettings from 'client/components/Tooltip/IconTooltipSettings';
import InputOptionalValidation from 'components/FormValidation/Input.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import KnowledgeBaseArticle from 'client/components/FormValidation/KnowledgeBaseArticle.vue';

const { error, success } = Constants.default;
const { ItemTypeConsts, StoreModelMap, StoreModuleMap } = Constants;
const applicationModel = StoreModelMap[ItemTypeConsts.application];
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

export default {
  name: 'ApplicationSSOConfiguration',

  inject: ['$validator'],

  components: {
    ApplicationDetailsMainDynamicControls,
    ApplicationDetailsSecondaryDynamicControls,
    ApplicationOIDCFields,
    FileInput,
    FlatActionButton,
    InputOptionalValidation,
    KnowledgeBaseArticle,
    IconButton,
  },

  mixins: [
    ApplicationConfigCertMixin,
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  learnMore: {
    infoText: 'To learn more about this configuration, including restricting access to specific users, please visit our',
    kbText: 'Knowledge Base',
  },

  data() {
    const fileInputTooltip = {
      kbLink: KbArticleUrls.ssoWithSamlConnector,
      text: IconTooltipSettings.spMetadataText,
    };

    return {
      fileInputTooltip,
      isApplicationDetails: true,
      dynamicComponentKey: 0,
    };
  },

  computed: {
    ...mapGetters(settingsModule, { betaFeatures: 'getBetaFeatures' }),

    ...mapState(settingsModule, ['orgId']),

    ...mapState(applicationModel, [
      'template',
    ]),

    ...mapGetters(applicationModel, [
      'isItemBeingCreated',
      'isItemBeingUpdated',
    ]),

    buttonStyleApplicationState() {
      const btnColors = ['btnDefault'];
      if (this.item.active) {
        if (!this.item.isBookmark) btnColors.push(this.$style.deactivateButtonStyle);
        return [...btnColors, 'btnAlert'];
      }
      return [...btnColors, 'btnCTA'];
    },

    buttonTextApplicationState() {
      const statusText = this.item.active ? 'Deactivate' : 'Activate';
      return this.item.isBookmark ? `${statusText} Bookmark` : `${statusText} SSO`;
    },

    enableMultiAcs() {
      return this.$hasFeatureFlag(this.$featureFlags.samlMultiAcs);
    },

    isJitSupported() {
      const { jit } = this.template;
      return jit && jit.attributes.length > 0;
    },

    learnMoreHref() {
      return this.item.learnMore || KbArticleUrls.gettingStartedSaml2SSO;
    },

    learnMoreJITHref() {
      return this.isJitSupported
        ? KbArticleUrls.samlConnectorWithJitAttributes
        : KbArticleUrls.samlConnectorWithoutJitAttributes;
    },

    metadataAction() {
      const metadataHref = `/api/organizations/${this.orgId}/applications/${this.item.id}/metadata.xml`;
      const metadataEndpoint = `/saml2/metadata/${this.item.id}`;
      return this.isItemBeingCreated || this.item.isBookmark ? null : {
        exportMetadata: {
          text: 'Export Metadata',
          handleClick: () => this.downloadFile(metadataHref, 'metadata.xml'),
        },
        copyMetadataUrl: {
          text: 'Copy Metadata URL',
          handleClick: () => {
            try {
              this.copyUrlToClipboard(metadataEndpoint);
              this.triggerNotification({
                showCloseButton: true,
                icon: 'info',
                message: 'Copied to clipboard.',
              });
            } catch (err) {
              this.triggerErrorNotification({
                message: 'Unable to copy to clipboard.',
                showCloseButton: true,
                icon: 'error',
              });
            }
          },
        },
      };
    },

    sectionHeading() {
      let ssoType;

      switch (true) {
        case this.item.isBookmark:
          ssoType = 'Bookmark';
          break;
        case this.item.sso.type === 'oidc':
          ssoType = 'OpenID Connect';
          break;
        case this.item.sso.type === 'saml':
        default:
          ssoType = 'Single Sign-On';
          break;
      }

      return `${ssoType} Configuration`;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  beforeMount() {
    const { isBookmark } = this.item;
    this.$validator.extend('isValidSSOFileName', {
      getMessage: `The ${isBookmark ? 'IdP' : ''} URL can only contain the following characters: A-Z, a-z, -, _`,
      validate: (value = '') => this.isValidSSOFileName(value),
    });

    this.$validator.extend('isValidUrl', {
      getMessage: 'URL must begin with https:// or http://',
      validate: (value = '') => this.isValidUrl(value),
    });
  },

  methods: {
    ...mapActions(applicationModel, [
      'setItemField',
    ]),
    ...mapActions(notificationModule, [
      'triggerNotification',
      'triggerErrorNotification',
    ]),

    cleanObject(obj) {
      const allowedKeys = Object.keys(this.item.config);
      Object.keys(obj).forEach((key) => {
        if (obj[key] === null || !allowedKeys.includes(key)) {
        // eslint-disable-next-line no-param-reassign
          delete obj[key];
        }
      });
      return obj;
    },

    /**
     * cleans the model by getting only the enabled attributes by the SAML server
     * @param base appliation model to clean
     */
    cleanXmlModelMetadata(base) {
      let item = cloneDeep(base);
      const { databaseAttributesAvailableKeys } = samlMetadata;
      const cleanAttribute = attr => databaseAttributesAvailableKeys.reduce((reducer, key) => ({
        [key]: attr[key],
        ...reducer,
      }), {});

      const cleanAttributes = (obj) => {
        const childModel = obj;
        const rootDatabaseAttributes = childModel.databaseAttributes || [];
        childModel.databaseAttributes = rootDatabaseAttributes.map(attr => cleanAttribute(attr));

        const { config = {} } = childModel;
        const { databaseAttributes } = config;
        if (databaseAttributes) {
          config.databaseAttributes.value = databaseAttributes.value.map(attr => cleanAttribute(attr));
        }

        return childModel;
      };

      // Figure out how the backbone model is adding these to the object (attributes/changed)
      item = cleanAttributes(item);
      return item;
    },

    handleApplicationState() {
      const status = !this.item.active;
      this.setItemField({ key: 'active', value: status });
    },

    handleFileChange(e, reader = new FileReader()) {
      const input = e.target;
      const { item } = this;
      const { files } = input;

      // Prepare the reader for persistence of the base64 file...
      // eslint-disable-next-line no-param-reassign
      reader.onload = (event) => {
        const xml = event.target.result;
        const attributes = this.cleanXmlModelMetadata(item);

        ApplicationApi.postSPMetadata(xml, attributes)
          .then((res) => {
            this.setItemField({ key: 'config', value: this.cleanObject(res.config) });
            this.dynamicComponentKey += 1;
            notify(success, 'Successfully read service provider metadata file.');
          })
          .catch((err) => {
            notify(error, err.message);
          });
      };

      if (files.length > 0) {
        const { size } = files[0];

        if (size > 1024 * 1024) {
          notify(error, 'Files uploaded must be smaller than 1 MB.');
          return;
        }
        reader.readAsText(files[0]);
      }
    },

    // Check to see if a string contains characters
    // that aren't considered valid (defined by RFC 3986)
    isValidSSOFileName(str) {
      const validLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
      return !some(str, x => validLetters.indexOf(x) === -1);
    },

    isValidUrl(value) {
      return value.startsWith('https://') || value.startsWith('http://');
    },
  },
};
/* eslint max-len: 0 */
</script>

<style module>
@import '../components/Buttons/FlatActionStyles.css';
@import '../components/FormValidation/css/formFields.css';
@import '../css/sections.css';

@value (gradientDark1, white) from '~css/colors.css';

.deactivateButtonStyle {
  width: 144px;
}

.idpUrlInput {
  width: 100%;
}

.ssoDirNameContainer {
  display: table-row;
  height: 48px;
}

.ssoDirNameContainer span {
  display: table-cell;
}

.ssoDirNameContainer div {
  display: table-cell;
}

.ssoDirNameContainer input {
  width: initial;
}

.exportMetadataContainer,
.uploadMetadataContainer {
  margin-bottom: 20px;
}

.uploadMetadataContainer label:first-of-type {
  margin: 0 0 3px 0;
}

.metadataActionButtonsContainer {
  column-gap: 10px;
  display: flex;
}

.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value] {
  position: relative;
}

.metadataInput [data-entry-key="spCertificate"] input[name=file] {
  display: none !important;
}

.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value]:not([value=""]) input[name=file] + label,
.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value=""] input[name=file][has-changed=true] + label {
  margin-top: 30px;
}

.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value]:not([value=""]) input[name=file] + label::before,
.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value=""] input[name=file][has-changed=true] + label::before {
  background: url('/img/icons/icon-file.svg') no-repeat;
  color: initial !important;
  display: block;
  font-weight: 800;
  height: 18px;
  left: 8px;
  overflow: hidden;
  position: absolute;
  text-indent: 22px;
  text-overflow: ellipsis;
  top: 26px;
  white-space: nowrap;
  width: 100%;
}

.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value] input[name=file][has-changed=true] + label::before {
  content: attr(data-name);
}

.metadataInput [data-entry-key="spCertificate"] div:first-of-type[value]:not([value=""]) input[name=file]:not([has-changed=true]) + label::before {
  content: 'SP Certificate has been uploaded';
}

.infoContainer p {
  color: gradientDark1 !important;
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
}

.infoContainer svg {
  height: 13px;
  margin-bottom: 3px;
}

.sectionGeneralMargin {
  margin-bottom: 20px !important;
}

</style>
